import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { FormProvider, useForm, Controller, type SubmitHandler, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import DatePicker from './DatePicker'
import TimeslotSelector from './TimeslotSelector'
import GuestSelector from './GuestSelector'
import ImageUpload from './ImageUpload'
import CustomerForm from './CustomerForm'
import AdditionalGuests from './AdditionalGuests'
import { bookingSchema } from '../utils/validation'
import type { TimeslotAvailability, BookingFormValues } from '../types/booking'
import { configureSupabaseClient, getSupabaseClient } from '../lib/supabase'
import { useWidgetStyle } from '../hooks/useWidgetStyle'
import { useSettings } from '../hooks/useSettings'

type ShopifyWidgetConfig = {
  supabaseUrl: string
  supabaseAnonKey: string
  shopifyVariantId: string
}

declare global {
  interface Window {
    __SUPERZ_CONFIG__?: ShopifyWidgetConfig
  }
}

const DEFAULT_WIDGET_COLORS = {
  primary: '#1a1f3a',
  secondary: '#d4af37',
  accent: '#f8f9fa',
}

const hexToRgbTriplet = (hex: string): string | null => {
  const sanitized = hex.replace('#', '')
  const fullHex =
    sanitized.length === 3
      ? sanitized
          .split('')
          .map((char) => char + char)
          .join('')
      : sanitized

  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) {
    return null
  }

  const bigint = parseInt(fullHex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `${r} ${g} ${b}`
}

export const BookingWidget = () => {
  const shopifyConfig = typeof window !== 'undefined' ? window.__SUPERZ_CONFIG__ : undefined
  const supabaseUrl = shopifyConfig?.supabaseUrl
  const supabaseAnonKey = shopifyConfig?.supabaseAnonKey

  useEffect(() => {
    if (supabaseUrl && supabaseAnonKey) {
      configureSupabaseClient(supabaseUrl, supabaseAnonKey)
    }
  }, [supabaseUrl, supabaseAnonKey])

  const shopifyVariantId = shopifyConfig?.shopifyVariantId
  const { settings, loading: settingsLoading } = useSettings()
  const { colors, loading: styleLoading } = useWidgetStyle()
  const methods = useForm<BookingFormValues>({
    resolver: zodResolver(bookingSchema) as Resolver<BookingFormValues>,
    mode: 'onBlur',
    defaultValues: {
      appointmentDate: null,
      timeslotId: '',
      guestCount: 1,
      name: '',
      email: '',
      phone: '',
      additionalGuests: [],
      image: null,
    } as BookingFormValues,
  })

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = methods

  const [timeslots, setTimeslots] = useState<TimeslotAvailability[]>([])
  const [timeslotLoading, setTimeslotLoading] = useState(false)
  const [timeslotError, setTimeslotError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const selectedDate = watch('appointmentDate')
  const selectedTimeslotId = watch('timeslotId')
  const guestCount = watch('guestCount')

  const selectedTimeslot = useMemo(
    () => timeslots.find((slot) => slot.id === selectedTimeslotId) ?? null,
    [selectedTimeslotId, timeslots],
  )

  useEffect(() => {
    console.log('[WIDGET] useEffect triggered, selectedDate:', selectedDate)
    const fetchTimeslots = async () => {
      if (!selectedDate) {
        console.log('[WIDGET] No selectedDate, clearing timeslots')
        setTimeslots([])
        return
      }

      setTimeslotLoading(true)
      setTimeslotError(null)

      try {
        const supabase = getSupabaseClient()
        const formattedDate = format(selectedDate, 'yyyy-MM-dd')
        console.log('[WIDGET] Fetching timeslots for:', formattedDate)
        console.log('[WIDGET] Calling Edge Function: get-available-timeslots')
        const { data, error } = await supabase.functions.invoke('get-available-timeslots', {
          body: { date: formattedDate },
        })
        console.log('[WIDGET] Edge Function response:', { data, error })

        if (error) {
          throw error
        }

        let payload: unknown[] = []
        if (data && typeof data === 'object' && 'available_slots' in data) {
          payload = Array.isArray((data as Record<string, unknown>).available_slots)
            ? ((data as Record<string, unknown>).available_slots as unknown[])
            : []
        } else if (Array.isArray(data)) {
          payload = data
        }

        console.log('[WIDGET] Parsed payload:', payload)
        const mapped = payload
          .map((slot) => normalizeTimeslot(slot))
          .filter((slot): slot is TimeslotAvailability => Boolean(slot))
          .sort((a, b) => compareTimes(a.startTime, b.startTime))

        setTimeslots(mapped)
        if (!mapped.some((slot) => slot.id === selectedTimeslotId)) {
          setValue('timeslotId', '')
        }
      } catch (err) {
        console.error(err)
        setTimeslotError(
          'We could not retrieve availability for the selected date. Please try again or choose a different date.',
        )
        setTimeslots([])
      } finally {
        setTimeslotLoading(false)
      }
    }

    fetchTimeslots()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate])

  useEffect(() => {
    if (!selectedTimeslot) return

    const remainingCapacity = selectedTimeslot.remainingSpots
    const effectiveMax = Math.min(settings.maxGuests, remainingCapacity)

    if (effectiveMax < 1) {
      setValue('timeslotId', '')
      setSubmitError('This timeslot is no longer available. Please choose another slot.')
      return
    }

    if (guestCount > effectiveMax) {
      setValue('guestCount', Math.max(1, effectiveMax), { shouldDirty: true, shouldValidate: true })
      trigger(['guestCount', 'additionalGuests'])
    }
  }, [guestCount, selectedTimeslot, settings.maxGuests, setValue, trigger])

  const handleTimeslotSelect = (timeslotId: string) => {
    setValue('timeslotId', timeslotId, { shouldDirty: true, shouldValidate: true })
    setSubmitError(null)
  }

  const onSubmit: SubmitHandler<BookingFormValues> = async (values) => {
    setSubmitError(null)

    if (!selectedTimeslot || !selectedTimeslot.id) {
      console.error('[WIDGET] Selected timeslot missing ID', selectedTimeslot)
      setSubmitError('Please select a timeslot for your experience.')
      return
    }

    const remainingCapacity = selectedTimeslot.remainingSpots
    if (values.guestCount > remainingCapacity) {
      setSubmitError('The selected timeslot no longer has enough capacity. Please choose another slot.')
      return
    }

    try {
      setSubmitting(true)
      const supabase = getSupabaseClient()

      const payload = {
        date: format(values.appointmentDate!, 'yyyy-MM-dd'),
        timeslotId: values.timeslotId,
        guestCount: values.guestCount,
        customerName: values.name,
        customerEmail: values.email,
        customerPhone: values.phone.trim(),
        imageUrl: values.image!.url,
        imageThumbnailUrl: values.image!.url,
        additionalGuests: values.additionalGuests.map((guest) => guest.trim()),
        totalPrice: values.guestCount * settings.pricePerPerson,
        ...(shopifyVariantId ? { shopifyVariantId } : {}),
      }
      console.log('[WIDGET] Submitting booking payload:', payload)

      const { data, error } = await supabase.functions.invoke<{ checkoutUrl?: string }>('create-booking', {
        body: payload,
      })

      if (error) {
        throw error
      }

      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl
      } else {
        setSubmitError('Your booking was received, but we could not open checkout automatically. Please contact support.')
      }
    } catch (err: unknown) {
      console.error(err)
      setSubmitError('We could not complete your booking. Please try again or reach out to info@superz.hu.')
    } finally {
      setSubmitting(false)
    }
  }

  const isolateStyle = useMemo(() => {
    const primaryRgb = hexToRgbTriplet(colors.primary) ?? hexToRgbTriplet(DEFAULT_WIDGET_COLORS.primary)!
    const secondaryRgb = hexToRgbTriplet(colors.secondary) ?? hexToRgbTriplet(DEFAULT_WIDGET_COLORS.secondary)!
    const accentRgb = hexToRgbTriplet(colors.accent) ?? hexToRgbTriplet(DEFAULT_WIDGET_COLORS.accent)!

    return {
      all: 'initial' as const,
      display: 'block',
      width: '100%',
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '0 20px',
      boxSizing: 'border-box' as const,
      '--widget-primary': colors.primary,
      '--widget-secondary': colors.secondary,
      '--widget-accent': colors.accent,
      '--widget-primary-rgb': primaryRgb,
      '--widget-secondary-rgb': secondaryRgb,
      '--widget-accent-rgb': accentRgb,
    } as CSSProperties & Record<string, string>
  }, [colors.primary, colors.secondary, colors.accent])

  if (styleLoading || settingsLoading) {
    return (
      <div className="superz-widget-panel flex w-full max-w-5xl items-center justify-center p-12">
        <span className="text-sm text-widget-accent/70">Personalising your experience…</span>
      </div>
    )
  }

  return (
    <div className="superz-widget-isolate" style={isolateStyle}>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="superz-widget-panel w-full max-w-5xl space-y-8 p-8"
        >
          <div className="flex flex-col gap-8 lg:grid lg:grid-cols-2">
            <Controller
              name="appointmentDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  selectedDate={field.value}
                  onChange={(nextDate) => {
                    field.onChange(nextDate)
                    setValue('timeslotId', '')
                    setTimeslots([])
                    setTimeslotError(null)
                    setSubmitError(null)
                  }}
                  closedDates={settings.closedDates}
                  openingHours={settings.openingHours}
                  minDate={new Date()}
                />
              )}
            />

            <TimeslotSelector
              selectedDate={selectedDate}
              timeslots={timeslots}
              selectedTimeslotId={selectedTimeslotId || null}
              onSelect={handleTimeslotSelect}
              loading={timeslotLoading}
              error={timeslotError ?? undefined}
            />
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            <Controller
              name="guestCount"
              control={control}
              render={({ field }) => (
                <GuestSelector
                  value={field.value}
                  onChange={(nextValue) => field.onChange(nextValue)}
                  minGuests={1}
                  maxGuests={settings.maxGuests}
                  remainingCapacity={selectedTimeslot?.remainingSpots ?? undefined}
                  pricePerGuest={settings.pricePerPerson}
                  error={errors.guestCount?.message}
                />
              )}
            />

            <Controller
              name="image"
              control={control}
              render={({ field }) => (
                <ImageUpload value={field.value} onChange={field.onChange} error={errors.image?.message as string} />
              )}
            />
          </div>

          <CustomerForm />
          <AdditionalGuests guestCount={guestCount} />

          <div className="superz-widget-panel flex flex-col gap-4 border border-widget-secondary/20 bg-widget-secondary/15 p-6 text-widget-accent">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/70">Summary</p>
                <h3 className="font-display text-3xl text-widget-accent">
                  {guestCount} guest{guestCount !== 1 && 's'} ·{' '}
                  {(guestCount * settings.pricePerPerson).toLocaleString('hu-HU')} Ft
                </h3>
              </div>

              <div className="flex flex-col gap-1 text-sm text-red-300">
                {!selectedDate && <span>⚠️ Please select a date first</span>}
                {selectedDate && !selectedTimeslotId && <span>⚠️ Please select a timeslot</span>}
                {!watch('image') && <span>⚠️ Please upload your SUPERZ parfume collection image</span>}
              </div>

              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-full border border-widget-secondary bg-widget-secondary px-8 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-widget-primary transition-all duration-200 shadow-[0_4px_12px_rgba(var(--widget-secondary-rgb),0.3)] hover:shadow-[0_8px_20px_rgba(var(--widget-secondary-rgb),0.4)] hover:scale-102 active:scale-98 focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary disabled:border-widget-accent/20 disabled:bg-widget-accent/20 disabled:text-widget-accent/40 disabled:shadow-none"
                disabled={
                  submitting ||
                  !selectedDate ||
                  !selectedTimeslotId ||
                  !watch('image')
                }
              >
                {submitting ? 'Reserving...' : 'Reserve Your Evening'}
              </button>
            </div>

            {submitError && (
              <p role="alert" className="rounded-2xl bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {submitError}
              </p>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export default BookingWidget

const normalizeTimeslot = (slot: any): TimeslotAvailability | null => {
  if (!slot) return null

  const startTime = slot.startTime ?? slot.start_time ?? ''
  const endTime = slot.endTime ?? slot.end_time ?? ''

  if (!startTime || !endTime) return null

  const capacity = Number(slot.capacity ?? slot.available_capacity ?? slot.max_capacity ?? slot.limit ?? 5)
  const remaining = Math.max(
    Number(
      slot.remainingSpots ??
        slot.remaining_spots ??
        slot.available_capacity ??
        slot.remaining ??
        slot.available ??
        capacity,
    ) || 0,
    0,
  )

  const id = typeof slot.id === 'string' ? slot.id : slot.id != null ? String(slot.id) : ''
  if (!id) {
    console.warn('[WIDGET] Received slot without ID from API', slot)
    return null
  }

  return {
    id,
    startTime,
    endTime,
    capacity: capacity || remaining || 5,
    remainingSpots: remaining || capacity || 5,
  }
}

const compareTimes = (a: string, b: string) => {
  const parseTime = (value: string) => {
    if (value.includes('T')) return new Date(value).getTime()
    const [hours, minutes] = value.split(':').map(Number)
    return (hours ?? 0) * 60 + (minutes ?? 0)
  }

  return parseTime(a) - parseTime(b)
}

